import { useAuth } from '@/contexts/auth-context';
import { useIsBrandManager } from '@/hooks/use-is-brand-manager';
import { type AffiliateUserListItem } from '@fluenceur/types';
import { Box } from '@chakra-ui/react';
import { useLayoutEffect, useRef, useState } from 'react';
import AffiliateProgramActionMenu from './AffiliateProgramActionMenu';
import AffiliateQuickActionDialog, {
  type AffiliateQuickActionRequest,
} from './AffiliateQuickActionDialog';

interface AffiliateProgramActionsProps {
  userId: string;
  affiliateName: string;
  programs: AffiliateUserListItem['programs'];
  onOpen: (programId: string) => void;
}

/** Every gesture names its own membership, even in the multi-programme table. */
export default function AffiliateProgramActions({
  userId,
  affiliateName,
  programs,
  onOpen,
}: AffiliateProgramActionsProps) {
  const { user } = useAuth();
  const { canManage } = useIsBrandManager();
  const trigger = useRef<HTMLButtonElement>(null);
  const [request, setRequest] = useState<AffiliateQuickActionRequest | null>(null);
  const availablePrograms = programs
    .map((program) => program.id)
    .sort()
    .join(':');
  useLayoutEffect(() => {
    setRequest((current) =>
      current &&
      current.brandId === user?.currentBrandId &&
      current.userId === userId &&
      availablePrograms.split(':').includes(current.programId)
        ? current
        : null
    );
  }, [user?.currentBrandId, userId, availablePrograms]);
  const currentRequest =
    request?.brandId === user?.currentBrandId &&
    request?.userId === userId &&
    programs.some((program) => program.id === request.programId)
      ? request
      : null;
  const choose = (
    program: AffiliateUserListItem['programs'][number],
    action: AffiliateQuickActionRequest['action']
  ) => {
    if (!user?.currentBrandId) return;
    setRequest({
      id: crypto.randomUUID(),
      brandId: user.currentBrandId,
      userId,
      affiliateName,
      programId: program.id,
      programName: program.name,
      action,
    });
  };
  return (
    <Box onClick={(event) => event.stopPropagation()}>
      <AffiliateProgramActionMenu
        key={`${user?.currentBrandId}:${userId}:${canManage}:${programs.map((program) => `${program.id}:${program.status}:${program.isActive}`).join(',')}`}
        triggerRef={trigger}
        programs={programs}
        canManage={canManage}
        onAction={(program, action) =>
          action === 'manage' ? onOpen(program.id) : choose(program, action)
        }
      />
      {currentRequest && (
        <AffiliateQuickActionDialog
          key={currentRequest.id}
          request={currentRequest}
          onClose={() => setRequest(null)}
          finalFocusEl={() => trigger.current}
        />
      )}
    </Box>
  );
}
